import { gql } from '@apollo/client';

const RECIPE_FIELDS = `
  id
  ownerUserId
  accessLevel
  sharedByName
  name
  description
  sourceUrl
  recipeBook
  category
  categories
  mealTypes
  status
  cuisine
  dietary
  tags
  difficulty
  prepTime
  cookTime
  totalTime
  servings
  rating
  favorite
  ingredients {
    id
    quantity
    unit
    item
    note
  }
  instructions {
    id
    text
  }
  notes
  calories
  protein
  coverPhotoUrl
  coverPhotoUpdatedAt
  cookingSummary { recipeId timesCooked lastCookedAt activeSessionId }
  updatedAt
`;

const RECIPE_LIST_FIELDS = `
  id
  ownerUserId
  accessLevel
  sharedByName
  name
  recipeBook
  category
  categories
  mealTypes
  status
  cuisine
  dietary
  tags
  difficulty
  prepTime
  cookTime
  totalTime
  servings
  rating
  favorite
  calories
  protein
  coverPhotoUrl
  coverPhotoUpdatedAt
  cookingSummary { recipeId timesCooked lastCookedAt activeSessionId }
  updatedAt
`;

export const GET_RECIPES = gql`
  query GetCookingRecipes($first: Int, $after: String) {
    getRecipesListPage(first: $first, after: $after) {
      nodes {
        ${RECIPE_LIST_FIELDS}
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

export const GET_RECIPE = gql`
  query GetRecipe($id: ID!) {
    getRecipe(id: $id) {
      ${RECIPE_FIELDS}
    }
  }
`;

export const ADD_RECIPE = gql`
  mutation AddRecipe($input: AddRecipeInput!) {
    addRecipe(input: $input) {
      ${RECIPE_FIELDS}
    }
  }
`;

export const UPDATE_RECIPE = gql`
  mutation UpdateRecipe($id: ID!, $input: UpdateRecipeInput!) {
    updateRecipe(id: $id, input: $input) {
      ${RECIPE_FIELDS}
    }
  }
`;


export const DELETE_RECIPE = gql`
  mutation DeleteRecipe($id: ID!) {
    deleteRecipe(id: $id)
  }
`;

export const IMPORT_RECIPE_FROM_URL = gql`
  mutation ImportRecipe($url: String!) {
    importRecipe(url: $url) {
      ${RECIPE_FIELDS}
    }
  }
`;

export const GET_RECIPE_SHARES = gql`
  query GetRecipeShares($recipeId: ID!) {
    getRecipeShares(recipeId: $recipeId) {
      id
      itemId
      granteeUserId
      granteeDisplayName
      granteeEmail
      permission
      status
      createdAt
    }
  }
`;

export const SHARE_RECIPE = gql`
  mutation ShareRecipe($input: ShareRecipeInput!) {
    shareRecipe(input: $input) {
      id
      itemId
      granteeUserId
      granteeDisplayName
      granteeEmail
      permission
      status
      createdAt
    }
  }
`;

export const REVOKE_RECIPE_SHARE = gql`
  mutation RevokeRecipeShare($id: ID!) {
    revokeRecipeShare(id: $id)
  }
`;

const COOKING_SESSION_FIELDS = `
  id recipeId recipeNameSnapshot recipePhotoSnapshot status startedAt completedAt cookedDate
  servingsMultiplier checkedIngredientIds completedInstructionIds currentInstructionId notes
  createdAt updatedAt
  ingredientsSnapshot { id quantity unit item note }
  instructionsSnapshot { id text }
`;

const COOKING_HISTORY_FIELDS = `
  id recipeId cookedDate completedAt servingsMultiplier notes createdAt updatedAt
`;

export const START_COOKING_SESSION = gql`
  mutation StartCookingSession($recipeId: ID!) {
    startCookingSession(recipeId: $recipeId) { ${COOKING_SESSION_FIELDS} }
  }
`;

export const UPDATE_COOKING_SESSION = gql`
  mutation UpdateCookingSession($id: ID!, $input: CookingSessionProgressInput!) {
    updateCookingSession(id: $id, input: $input) { ${COOKING_SESSION_FIELDS} }
  }
`;

export const FINISH_COOKING_SESSION = gql`
  mutation FinishCookingSession($id: ID!, $input: FinishCookingSessionInput) {
    finishCookingSession(id: $id, input: $input) { ${COOKING_SESSION_FIELDS} }
  }
`;

export const DISCARD_COOKING_SESSION = gql`
  mutation DiscardCookingSession($id: ID!) { discardCookingSession(id: $id) }
`;

export const LOG_PAST_COOKING_SESSION = gql`
  mutation LogPastCookingSession($recipeId: ID!, $input: PastCookingSessionInput!) {
    logPastCookingSession(recipeId: $recipeId, input: $input) { ${COOKING_SESSION_FIELDS} }
  }
`;

export const GET_RECIPE_COOKING_HISTORY = gql`
  query GetRecipeCookingHistory($recipeId: ID!, $first: Int, $after: String) {
    getRecipeCookingHistory(recipeId: $recipeId, first: $first, after: $after) {
      nodes { ${COOKING_HISTORY_FIELDS} }
      pageInfo { hasNextPage endCursor }
    }
  }
`;
